import type { Data } from '@dnd-kit/abstract';
import type { Draggable, Droppable } from '@dnd-kit/dom';
import type { OptionQuestionTranslate } from '@repo/types/questions/QuestionTranslate';

import { arrayMove } from '@dnd-kit/helpers';
import { isSortable } from '@dnd-kit/react/sortable';
import { QuestionTranslate } from '@repo/lib/quiz/questions/class/QuestionTranslate';
import { shuffleArray } from '@repo/lib/shuffleArray';
import { useMemo, useState } from 'react';

import type { QuestionTranslateProps } from '@/components/features/quiz/question/question-resolver/question-translate';

import { useQuestionState } from '@/hooks/questions';

export function useQuestionTranslate(props: QuestionTranslateProps) {
  const { question, sectionIndex, questionIndex } = props;
  const { getMarked, getContent, getAnswer, setAnswer, exportedAnswer } = useQuestionState<QuestionTranslate>(question, sectionIndex, questionIndex);
  const content = getContent();
  const selectedAnswerIds: number[] = getAnswer() || [];
  const marked = getMarked();
  const exportedUserAnswer = exportedAnswer();

  const [shuffledOptions] = useState<OptionQuestionTranslate[]>(() => shuffleArray(content.options));
  const selectedOptions = useMemo<OptionQuestionTranslate[]>(
    () => selectedAnswerIds.map((id) => content.options.find((option) => option.id === id)).filter(Boolean) as OptionQuestionTranslate[],
    [selectedAnswerIds, content.options]
  );
  const availableOptions = useMemo<OptionQuestionTranslate[]>(() => {
    const selectedIdsSet = new Set(selectedAnswerIds);
    return shuffledOptions.filter((option) => !selectedIdsSet.has(option.id));
  }, [shuffledOptions, selectedAnswerIds]);

  const addOptionToSelected = (option: OptionQuestionTranslate) => {
    setAnswer([...selectedAnswerIds, option.id]);
  };
  const removeOptionFromSelected = (option: OptionQuestionTranslate) => {
    setAnswer(selectedAnswerIds.filter((id) => id !== option.id));
  };

  const handleDragEnd = (target: Droppable<Data> | null, source: Draggable<Data> | null) => {
    if (!target || !source) return;
    if (target.id === 'answer-droppable-box') {
      const option = availableOptions.find((w) => w.id === source.id);
      if (option) {
        addOptionToSelected(option);
      }
      return;
    }

    if (target.id === 'dataset-droppable-box') {
      const option = selectedOptions.find((w) => w.id === source.id);
      if (option) {
        removeOptionFromSelected(option);
      }
      return;
    }

    if (!isSortable(source)) {
      return;
    }

    const oldIndex: number = selectedAnswerIds.indexOf(Number(source.id));
    const newIndex: number = source.sortable.index;

    const reordered: number[] = arrayMove(selectedAnswerIds, oldIndex, newIndex);
    setAnswer(reordered);
  };

  return {
    marked,
    availableOptions,
    selectedOptions,
    handleDragEnd,
    addOptionToSelected,
    removeOptionFromSelected,
    exportedUserAnswer,
  };
}
